import { Injectable } from '@angular/core';
import { COLOR_STEPS, COLOR_TYPES, ColorStep, ColorType } from '../models';
import { HEXColor, RGBAColor, RGBColor, TailwindColor } from '../models/basic/color';

@Injectable({
  providedIn: 'root'
})
export class ColorService {
  /**
   * Generates the contrast-color for the given color
   * @param color The hex- or tailwind-color to generate the contrast-color for
   */
  static getContrastColorOf<T extends TailwindColor | HEXColor>(color: T): T {
    if (color === undefined || color === null) return color;
    if (isTailwindColor(color)) {
      const colorType = color.split('-')[0];
      if (!COLOR_TYPES.includes(colorType as ColorType)) return color;
      return `${colorType}-contrast` as T;
    } else if (isHexColor(color)) {
      const luminance = this.getLuminanceOfRGB(this.hexToRgb(color));
      return luminance > 0.5 ? ('#000000' as T) : ('#ffffff' as T);
    }
    return color;
  }

  /**
   * Instance wrapper for templates expecting an instance method.
   * Delegates to the static implementation to keep a single source of truth.
   */
  getContrastColorOf<T extends TailwindColor | HEXColor>(color: T): T {
    return ColorService.getContrastColorOf(color);
  }

  /**
   * Calculates the luminance of a given RGB-color
   * @param rgb The RGB-color to calculate the luminance for
   * @returns The luminance of the given RGB-color
   */
  static getLuminanceOfRGB(rgb: RGBColor): number {
    return (0.2126 * rgb.r + 0.7152 * rgb.g + 0.0722 * rgb.b) / 255;
  }

  /**
   * Converts a hex-color to an RGB-color object
   * @param hex The hex-color to convert
   * @returns The RGB-color object
   */
  static hexToRgb(hex: HEXColor): RGBColor {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return result
      ? {
          r: parseInt(result[1], 16),
          g: parseInt(result[2], 16),
          b: parseInt(result[3], 16)
        }
      : { r: 0, g: 0, b: 0 };
  }

  /**
   * Converts a 3-, 4-, 6- or 8-digit HEX color to RGBA channels.
   * @param hex The hex-color to convert
   * @returns The RGBA-color, alpha in the 0–1 range (1 when the hex has no alpha channel)
   */
  static hexToRgba(hex: HEXColor): RGBAColor {
    let digits = hex.replace('#', '');
    if (digits.length === 3 || digits.length === 4) {
      digits = digits
        .split('')
        .map((char) => char + char)
        .join('');
    }
    return {
      r: parseInt(digits.slice(0, 2), 16),
      g: parseInt(digits.slice(2, 4), 16),
      b: parseInt(digits.slice(4, 6), 16),
      a: digits.length >= 8 ? parseInt(digits.slice(6, 8), 16) / 255 : 1
    };
  }

  /**
   * Converts RGB-colors to a hex-color
   * @param r Red value
   * @param g Green value
   * @param b Blue value
   * @returns The hex-color
   */
  static rgbToHex(r: number, g: number, b: number): HEXColor {
    return ('#' + ((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1)) as HEXColor;
  }

  /**
   * Interpolates between two colors
   * @param color1 First color
   * @param color2 Second color
   * @param factor Factor to interpolate
   * @returns The interpolated color as HEX-color
   */
  static interpolateColor(color1: HEXColor, color2: HEXColor, factor: number): HEXColor {
    const c1 = this.hexToRgb(color1);
    const c2 = this.hexToRgb(color2);
    const r = Math.round(c1.r + factor * (c2.r - c1.r));
    const g = Math.round(c1.g + factor * (c2.g - c1.g));
    const b = Math.round(c1.b + factor * (c2.b - c1.b));
    return this.rgbToHex(r, g, b);
  }

  /**
   * Converts a ColorType to a set of Tailwind colors for each ColorStep
   * @param color The ColorType to convert
   * @returns An object mapping each ColorStep to its corresponding TailwindColor
   */
  static colorTypeToColor(color: ColorType): Record<ColorStep, TailwindColor> {
    return {
      shine: `${color}-shine` as TailwindColor,
      main: `${color}-main` as TailwindColor,
      shade: `${color}-shade` as TailwindColor,
      contrast: `${color}-contrast` as TailwindColor
    };
  }

  static isTailwindColor(color: string): boolean {
    if (color?.startsWith('#')) return false;
    const parts = color.split('-');
    const colorType = parts[0];
    const colorStep = parts[1];
    return typeof colorType === 'string' && (COLOR_STEPS.includes(colorStep as ColorStep) || Number(colorStep) >= 50);
  }

  /**
   * Tests for a valid hex-color format (3, 4, 6 or 8 digits — the 4/8-digit forms carry an alpha channel)
   * @param color The color to test
   * @returns True if the color is a valid hex-color, false otherwise
   */
  static isHexColor(color: string): boolean {
    return /^#([0-9A-Fa-f]{3,4}|[0-9A-Fa-f]{6}|[0-9A-Fa-f]{8})$/.test(color);
  }
}

/**
 * Type guard for HEXColor
 * @param color The color to test
 * @returns True if the color is a valid HEXColor, false otherwise
 */
export function isTailwindColor(color: string): color is TailwindColor {
  return ColorService.isTailwindColor(color);
}

/**
 * Type guard for HEXColor
 * @param color The color to test
 * @returns True if the color is a valid HEXColor, false otherwise
 */
export function isHexColor(color: string): color is HEXColor {
  return ColorService.isHexColor(color);
}
